import React from 'react';
import { TableCell, TableRow } from '@mui/material';
import type { UsageActionRow } from '../types';
import { formatCount, elementKindLabel } from '../services/usageFormat';
import SchutzZeile from './SchutzZeile';
import DatenTabelle, { zahlenZelle } from './DatenTabelle';
import { nutzerText } from './SeitenTabelle';

/**
 * RITA-1505 (Nutzungserfassung Phase 2c) — genutzte Bedienvorgänge.
 *
 * Zwei Ereignisarten in einer Tabelle: Klicks auf Bedienelemente und gemeldete Vorgänge aus dem
 * Code (useTrack). Sie kommen aus zwei Abfragen desselben Dokuments, weil der Server VOR der
 * Ausgabe nach Anzahl vorauswählt — ein Aufruf ohne Filter lieferte nicht dieselben Zeilen.
 *
 * Die Route steht als eigene Spalte, weil der Server je Route gruppiert: 'berater.speichern' auf
 * drei Routen sind drei Zeilen. Zusammenfassen ginge nur für die Anzahl — die Nutzerzahlen ließen
 * sich nicht addieren, weil die Schnittmenge der Pseudonyme unbekannt ist.
 *
 * Es gibt bewusst KEINE Liste „nie genutzt“: was weniger als fünf Personen benutzen, erscheint in
 * der Sammelzeile ohne Namen — „selten“ wäre von „nie“ nicht zu unterscheiden.
 */
export interface FunktionenTabelleProps {
  klicks: UsageActionRow[];
  aktionen: UsageActionRow[];
}

/** Beide Ereignisarten in einer nach Anzahl absteigenden Liste; bei Gleichstand nach Bezeichnung. */
export const zusammenfuehren = (klicks: UsageActionRow[], aktionen: UsageActionRow[]): UsageActionRow[] =>
  [...klicks, ...aktionen].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

export default function FunktionenTabelle({ klicks, aktionen }: FunktionenTabelleProps) {
  const rows = zusammenfuehren(klicks, aktionen);
  return (
    <DatenTabelle
      leer={rows.length === 0}
      leerText="Im Zeitraum sind keine Bedienvorgänge verdichtet."
      spalten={[
        { titel: 'Schlüssel' },
        { titel: 'Route' },
        { titel: 'Art' },
        { titel: 'Anzahl', numerisch: true },
        { titel: 'Nutzer', numerisch: true },
      ]}
    >
      {rows.map((row, stelle) => (
        // eslint-disable-next-line react/no-array-index-key
        <TableRow key={`${row.label}-${stelle}`}>
          <TableCell>
            <SchutzZeile
              suppressed={row.suppressed}
              groups={row.groups}
              users={row.users}
              bezeichner={row.name}
              technisch
            />
          </TableCell>
          <TableCell>
            {row.suppressed ? (
              '–'
            ) : (
              <SchutzZeile suppressed={false} groups={row.groups} users={row.users} bezeichner={row.route} technisch />
            )}
          </TableCell>
          {/* Die Art steckt in `extra.element_kind` und gibt es nur bei Klicks. Gemeldete
              Vorgänge haben kein Bedienelement, Sammelzeilen tragen kein `extra` — dort bleibt
              die Spalte leer, statt eine Art zu raten. */}
          <TableCell>{row.suppressed ? '–' : elementKindLabel(row.extra)}</TableCell>
          <TableCell sx={zahlenZelle}>{formatCount(row.count)}</TableCell>
          <TableCell sx={zahlenZelle}>{nutzerText(row.users, row.usersEstimated)}</TableCell>
        </TableRow>
      ))}
    </DatenTabelle>
  );
}
